import { Box, useMediaQuery } from '@mui/material'
import update from 'immutability-helper'
import PropTypes from 'prop-types'
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { useFilters, useGlobalFilter, useTable } from 'react-table'
import { motion } from 'framer-motion'

import TableHeader from '../../../components/shared/table/components/table-header.component'
import TableDesktopContent from '../../../components/shared/table/components/table-desktop-content.component'
import TableDesktopFilters from '../../../components/shared/table/components/filters/table-filter-desktop.component/table-filter-desktop.component'
import TableMobileSearch from '../../../components/shared/table/components/filters/table-mobile-search/table-mobile-search.component'
import TableTabletSearchInput from '../../../components/shared/table/components/filters/table-tablet-filter.component'

import { useDialog } from '../dialog/dialog.hooks'
import { styles } from '../table/table.styles'

import { useTableFilters } from './hooks/use-table-filters.hook'
import { useInfiniteScroll } from './hooks/use-table-infinite-scroll.hook'
import { FILTER_MODES, FILTER_SELECT_TYPES, SORTING_DIRECTIONS } from './table.constants'
import TableMobileFilterDialog from './components/filters/table-mobile-filter-dialog.component'
import TablePagination from './components/table-pagination.component'

// Animation variants for table fade-in
const tableFadeVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3, ease: 'easeOut' } },
}

const Table = forwardRef(
  (
    {
      data,
      columns,
      title,
      description,
      toolbar,
      toolbarWithBorder,
      primaryAction,
      secondaryAction,
      pagination,
      searchPlaceholder,
      hasGlobalFilter,
      hasDragAndDrop,
      onDragAndDrop,
      filterMode,
      onSearch,
      onSearchKeyDown,
      onSearchIconClick,
      onClearSearchRef,
      isLoading = false,
      isFiltersLoading,
      onColumnFilter,
      filterSelectType,
      sorting,
      infiniteScrollProps,
      columnFilter,
      hidePaginationUI,
      helperText,
      mobileTableView,
      tabletTableView,
      otherActions,
      customActions,
      muiSelectProps,
      searchValue,
      mobileFilterTitle,
      onMobileFilterChange,
      onMobileFilterCancel,
      shouldTabletSearchInput,
      isSearchDisabled,
      customHeaderStyles,
      customEmptyState,
      searchEndAdornment,
      searchSlot,
      contentAfterSearch,
      searchIconPosition,
      ...props
    },
    ref
  ) => {
    const [records, setRecords] = useState(data)
    const recordsRef = React.useRef(records)
    const isDraggingRef = React.useRef(false)
    const originalOrderRef = React.useRef(null)
    const pendingOrderRef = React.useRef(null)
    const [isMobileFilterDialogOpen, setIsMobileFilterDialogOpen] = useState(false)
    const originalFiltersRef = React.useRef(null)
    const previousColumnsHashRef = React.useRef(null)
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))
    const isTablet = useMediaQuery(
      (theme) => theme.breakpoints.up('sm') && theme.breakpoints.down('md')
    )
    const { pushDialog, closeDialog, popDialog, dialogState } = useDialog()
    const { tableEl } = useInfiniteScroll()

    useEffect(() => {
      if (isDraggingRef.current) return
      if (pendingOrderRef.current && data?.length > 0) {
        const incomingOrder = data.map((r) => r.id).join(',')
        if (incomingOrder === pendingOrderRef.current) return
        pendingOrderRef.current = null
      }
      setRecords(data)
      recordsRef.current = data
    }, [data])

    const initialState = {
      hiddenColumns: columns
        .filter((col) => col.isVisible !== undefined && col.isVisible === false)
        .map((col) => col.accessor),
    }

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      columns: tableColumns,
      prepareRow,
      setGlobalFilter,
      state,
      setFilter,
      setAllFilters,
    } = useTable(
      {
        columns,
        data: records,
        initialState,
      },
      useFilters,
      useGlobalFilter
    )

    // Merge original columns with tableColumns to get both custom props and react-table props
    const mergedColumns = useMemo(() => {
      return tableColumns.map((tableColumn) => {
        const originalColumn = columns.find((col) => col.accessor === tableColumn.id)
        const merged = {
          ...tableColumn,
          ...originalColumn,
        }
        // For server-side filters, sync filterValue from columnFilter so dropdowns reflect the current selection
        if (filterMode === FILTER_MODES.SERVER_SIDE && columnFilter?.[tableColumn.id]) {
          const filterEntry = columnFilter[tableColumn.id]
          merged.filterValue = typeof filterEntry === 'object' ? filterEntry.value : filterEntry
        }
        return merged
      })
    }, [tableColumns, columns, filterMode, columnFilter])

    const memoizedOnSearch = useCallback(
      (value) => {
        if (onSearch) onSearch(value)
      },
      [onSearch]
    )

    const {
      handleColumnFilter,
      clearFilters,
      handleGlobalFilter,
      shouldClearFilters,
      setShouldClearFilters,
    } = useTableFilters(
      memoizedOnSearch,
      tableColumns,
      pagination,
      setGlobalFilter,
      onColumnFilter,
      setFilter,
      setAllFilters,
      filterMode
    )

    const hasToolbar = Boolean(toolbar || primaryAction || secondaryAction || pagination)

    const moveRow = (dragIndex, hoverIndex) => {
      if (!isDraggingRef.current) {
        originalOrderRef.current = records.map((r) => r.id)
      }
      isDraggingRef.current = true
      const dragRecord = records[dragIndex]
      const updatedRecords = update(records, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRecord],
        ],
      })
      setRecords(updatedRecords)
      recordsRef.current = updatedRecords
    }

    const handleDrop = useCallback(() => {
      const currentOrder = recordsRef.current.map((r) => r.id)
      const orderChanged =
        !originalOrderRef.current ||
        currentOrder.some((id, i) => id !== originalOrderRef.current[i])

      if (orderChanged) {
        pendingOrderRef.current = recordsRef.current.map((r) => r.id).join(',')
        onDragAndDrop?.(recordsRef.current)
      }
      isDraggingRef.current = false
      originalOrderRef.current = null
    }, [onDragAndDrop])

    // Calculate active filter count early so it can be used in callbacks
    const isActiveFilter = (filter) => {
      if (!filter || typeof filter !== 'object') return false
      const value = filter?.value

      if (Array.isArray(filter)) {
        return filter.some(
          (item) =>
            item?.value && (Array.isArray(item.value) ? item.value.length : Boolean(item.value))
        )
      }

      return Array.isArray(value)
        ? value.length > 0
        : Boolean(value) || Object.keys(filter).length > 0
    }

    const activeFilterCount = Object.values(columnFilter || {}).filter(isActiveFilter).length

    // Define handleDialogClose early so it can be used in other functions
    const handleDialogClose = useCallback(() => {
      setIsMobileFilterDialogOpen(false)
      // Revert to original filters if cancel is clicked
      if (onMobileFilterCancel && originalFiltersRef.current) {
        onMobileFilterCancel(originalFiltersRef.current)
      }
      originalFiltersRef.current = null // Clear the ref
      closeDialog()
    }, [onMobileFilterCancel, closeDialog])

    // Wrap handleColumnFilter to detect Apply clicks
    const wrappedHandleColumnFilter = useCallback(
      (...args) => {
        // When setFilter is called (from Apply button), clear the original ref
        // so that closeDialog won't revert the changes
        originalFiltersRef.current = null
        return handleColumnFilter(...args)
      },
      [handleColumnFilter]
    )

    const handleClearMobileFilters = useCallback(() => {
      clearFilters()
      // Clear the original filters ref since user explicitly cleared
      originalFiltersRef.current = null
      const clearedColumns = mergedColumns.map((column) => ({
        ...column,
        filterValue: undefined,
      }))
      const dialogProps = {
        closeDialog: () => {
          setIsMobileFilterDialogOpen(false)
          closeDialog()
        },
        columns: clearedColumns,
        setFilter: wrappedHandleColumnFilter,
        filterMode,
        mobileFilterTitle,
        onClearMobileFilters: handleClearMobileFilters,
        onFilterChange: onMobileFilterChange,
        key: Date.now(),
      }
      popDialog()
      pushDialog(TableMobileFilterDialog, dialogProps, {
        activeFilterCount,
      })
    }, [
      clearFilters,
      mergedColumns,
      wrappedHandleColumnFilter,
      filterMode,
      mobileFilterTitle,
      onMobileFilterChange,
      popDialog,
      pushDialog,
      activeFilterCount,
      closeDialog,
    ])

    // Update mobile filter dialog when columns change while it's open
    useEffect(() => {
      if (!isMobileFilterDialogOpen) {
        return
      }

      // Create a hash of important column properties to detect meaningful changes
      const columnsHash = JSON.stringify(
        mergedColumns
          .filter((col) => col.hasFilter)
          .map((col) => ({
            id: col.id,
            disabled: col.disabled,
            optionsCount: col.serverSideFilterOptions?.length || 0,
          }))
      )

      // Only update if columns actually changed
      if (columnsHash !== previousColumnsHashRef.current) {
        previousColumnsHashRef.current = columnsHash

        // Re-push dialog with merged columns (has both react-table props and custom props like disabled)
        popDialog()
        const updatedDialogState = {
          appliedFilters: columnFilter,
        }
        pushDialog(
          TableMobileFilterDialog,
          {
            closeDialog: handleDialogClose,
            columns: mergedColumns,
            setFilter: wrappedHandleColumnFilter,
            filterMode,
            mobileFilterTitle,
            onClearMobileFilters: handleClearMobileFilters,
            onFilterChange: onMobileFilterChange,
            activeFilterCount,
          },
          updatedDialogState
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      mergedColumns,
      isMobileFilterDialogOpen,
      handleDialogClose,
      wrappedHandleColumnFilter,
      handleClearMobileFilters,
    ])

    const onPushDialog = useCallback(() => {
      // Save original filter state when dialog first opens
      originalFiltersRef.current = { ...columnFilter }

      const dialogState = {
        appliedFilters: columnFilter,
      }

      setIsMobileFilterDialogOpen(true)
      pushDialog(
        TableMobileFilterDialog,
        {
          closeDialog: handleDialogClose,
          columns: mergedColumns,
          setFilter: wrappedHandleColumnFilter,
          filterMode,
          mobileFilterTitle,
          onClearMobileFilters: handleClearMobileFilters,
          onFilterChange: onMobileFilterChange,
          activeFilterCount,
        },
        dialogState
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      columnFilter,
      mergedColumns,
      filterMode,
      mobileFilterTitle,
      activeFilterCount,
      handleDialogClose,
      wrappedHandleColumnFilter,
    ])

    const hasColumnFilters = columns.some((item) => item.hasFilter)

    const filterProps = {
      columns,
      state,
      handleGlobalFilter,
      searchPlaceholder,
      hasGlobalFilter,
      clearFilters,
      activeFilterCount,
      onPushDialog,
      handleColumnFilter,
      filterMode,
      shouldClearFilters,
      setShouldClearFilters,
      tableColumns: mergedColumns,
      isFiltersLoading,
      muiSelectProps,
      initialValue: searchValue,
      setPage: pagination?.setPage,
      isSearchDisabled,
      searchEndAdornment,
      hasToolbarWithBorder: toolbarWithBorder,
      searchSlot,
      onKeyDown: onSearchKeyDown,
      onSearchIconClick,
      onClearSearchRef,
      contentAfterSearch,
      searchIconPosition,
    }
    const areAllFiltersDisabled = useMemo(
      () => columns.filter((item) => item.hasFilter).every((item) => item.disabled),
      [columns]
    )

    return (
      <>
        <TableHeader
          title={title}
          description={description}
          hasToolbar={hasToolbar}
          toolbarWithBorder={toolbarWithBorder}
          toolbar={toolbar}
          pagination={pagination}
          hidePaginationUI={hidePaginationUI}
          secondaryAction={secondaryAction}
          primaryAction={primaryAction}
          ref={ref}
          helperText={helperText}
          otherActions={otherActions}
          customActions={customActions}
          filterProps={{
            onPushFilterDialog: hasColumnFilters && onPushDialog,
            activeFilterCount,
            toolbarWithBorder,
          }}
          areAllFiltersDisabled={areAllFiltersDisabled}
          customHeaderStyles={customHeaderStyles}
        />

        {isMobile && mobileTableView ? (
          <Box
            component={motion.div}
            variants={tableFadeVariants}
            initial="hidden"
            animate="visible"
          >
            {hasGlobalFilter && (
              <TableMobileSearch
                {...filterProps}
                isSearchDisabled={isSearchDisabled}
                searchEndAdornment={searchEndAdornment}
                showSearchFieldBorder={false}
              />
            )}
            {mobileTableView}
          </Box>
        ) : isTablet && tabletTableView ? (
          <Box
            component={motion.div}
            variants={tableFadeVariants}
            initial="hidden"
            animate="visible"
          >
            {hasGlobalFilter && (
              <TableTabletSearchInput
                {...filterProps}
                isSearchDisabled={isSearchDisabled}
                searchEndAdornment={searchEndAdornment}
                hasTabletTableView
              />
            )}
            <Box sx={styles.customTableTabletView}>{tabletTableView}</Box>
            <Box sx={styles.tabletPagination}>
              {pagination && (
                <TablePagination hidePaginationUI={hidePaginationUI} {...pagination} />
              )}
            </Box>
          </Box>
        ) : (
          <Box
            component={motion.div}
            variants={tableFadeVariants}
            initial="hidden"
            animate="visible"
          >
            {isTablet && shouldTabletSearchInput ? (
              hasGlobalFilter && (
                <TableTabletSearchInput {...filterProps} paginationProps={pagination} />
              )
            ) : (
              <TableDesktopFilters
                {...filterProps}
                showSearchFieldBorder={false}
                areAllFiltersDisabled={areAllFiltersDisabled}
              />
            )}
            <TableDesktopContent
              getTableProps={getTableProps}
              getTableBodyProps={getTableBodyProps}
              headerGroups={headerGroups}
              rows={rows}
              prepareRow={prepareRow}
              moveRow={moveRow}
              onDrop={handleDrop}
              hasDragAndDrop={hasDragAndDrop}
              sorting={sorting}
              isLoading={isLoading}
              infiniteScrollProps={infiniteScrollProps}
              customEmptyState={customEmptyState}
              ref={infiniteScrollProps?.ref || tableEl}
              {...props}
            />
            <Box sx={styles.tabletPagination}>
              {isTablet && pagination && (
                <TablePagination hidePaginationUI={hidePaginationUI} {...pagination} />
              )}
            </Box>
          </Box>
        )}
      </>
    )
  }
)

Table.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  toolbar: PropTypes.node,
  toolbarWithBorder: PropTypes.bool,
  primaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    action: PropTypes.func.isRequired,
  }),
  secondaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    action: PropTypes.func.isRequired,
    props: PropTypes.object,
  }),
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    onPageSizeChange: PropTypes.func.isRequired,
    pageSizeOptions: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      })
    ),
    canGoBack: PropTypes.bool.isRequired,
    canGoForward: PropTypes.bool.isRequired,
    onNextPage: PropTypes.func.isRequired,
    onPreviousPage: PropTypes.func.isRequired,
    currentPageRecords: PropTypes.number.isRequired,
    totalResultsQuantity: PropTypes.number,
    setPage: PropTypes.func,
  }),
  searchPlaceholder: PropTypes.string,
  hasGlobalFilter: PropTypes.bool,
  onDragAndDrop: PropTypes.func,
  hasDragAndDrop: PropTypes.bool,
  filterMode: PropTypes.oneOf(Object.values(FILTER_MODES)),
  onSearch: PropTypes.func,
  onSearchKeyDown: PropTypes.func,
  onSearchIconClick: PropTypes.func,
  onClearSearchRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.func }),
  ]),
  searchValue: PropTypes.string,
  isLoading: PropTypes.bool,
  isFiltersLoading: PropTypes.bool,
  onColumnFilter: PropTypes.func,
  filterSelectType: PropTypes.oneOf(Object.values(FILTER_SELECT_TYPES)),
  sorting: PropTypes.shape({
    onSort: PropTypes.func,
    direction: PropTypes.oneOf(Object.values(SORTING_DIRECTIONS)),
    field: PropTypes.string,
  }),
  infiniteScrollProps: PropTypes.shape({
    hasMore: PropTypes.bool,
    next: PropTypes.func,
    isRefetching: PropTypes.bool,
    infiniteScroll: PropTypes.bool,
    ref: PropTypes.shape({ current: PropTypes.any }),
  }),
  columnFilter: PropTypes.object,
  hidePaginationUI: PropTypes.bool,
  helperText: PropTypes.string,
  mobileTableView: PropTypes.node,
  tabletTableView: PropTypes.node,
  otherActions: PropTypes.node,
  customActions: PropTypes.node,
  muiSelectProps: PropTypes.object,
  mobileFilterTitle: PropTypes.string,
  onMobileFilterChange: PropTypes.func,
  onMobileFilterCancel: PropTypes.func,
  shouldTabletSearchInput: PropTypes.bool,
  show: PropTypes.bool,
  isSearchDisabled: PropTypes.bool,
  customHeaderStyles: PropTypes.object,
  customEmptyState: PropTypes.node,
  searchEndAdornment: PropTypes.node,
  searchSlot: PropTypes.node,
  contentAfterSearch: PropTypes.node,
  searchIconPosition: PropTypes.oneOf(['start', 'end']),
}

Table.defaultProps = {
  hidePaginationUI: false,
  shouldTabletSearchInput: true,
}

export default Table
